import { PostgrestClient } from "@supabase/postgrest-js";
import { Trip, TripWithTenant } from "../types";
import { isInfinityDate } from "../utils/dateHelpers";

export class TripService {
  private postgrest: PostgrestClient;

  constructor(postgrest: PostgrestClient) {
    this.postgrest = postgrest;
  }

  /**
   * Create a new trip
   * 
   * NOTE: This only creates the trip record without any placement.
   * To assign it to a location, use assignToLocation() after creation,
   * or use createTripWithLocation() for atomic creation with initial assignment.
   * 
   * @param trip - Trip data (must include tenant_id, fbo_id, arrival_date, departure_date)
   * @returns Created trip with generated ID and version=1
   * @throws Error if dates are invalid or required fields missing
   */
  async createTrip(trip: Partial<Trip>): Promise<Trip> {
    // Validate required fields
    if (!trip.tenant_id) {
      throw new Error("tenant_id is required");
    }
    if (!trip.fbo_id) {
      throw new Error("fbo_id is required");
    }
    if (!trip.arrival_date) {
      throw new Error("arrival_date is required");
    }
    if (!trip.departure_date) {
      throw new Error("departure_date is required");
    }

    // Validate date logic
    const arrival = new Date(trip.arrival_date);

    if (isNaN(arrival.getTime())) {
      throw new Error("Invalid arrival_date format");
    }

    // Skip departure validation if it's infinity (base tenant / indefinite stay)
    if (!isInfinityDate(trip.departure_date)) {
      const departure = new Date(trip.departure_date);

      if (isNaN(departure.getTime())) {
        throw new Error("Invalid departure_date format");
      }
      if (arrival >= departure) {
        throw new Error("arrival_date must be before departure_date");
      }
    }

    // Execute insert
    const { data, error } = await this.postgrest
      .from("trip")
      .insert(trip)
      .select()
      .single();

    if (error) {
      // Check for specific constraint violations
      if (error.message.includes("overlapping")) {
        throw new Error(`Trip overlaps with existing trip for this tenant`);
      }
      throw new Error(`Failed to create trip: ${error.message}`);
    }

    return data;
  }

  /**
   * Update an existing trip
   */
  async updateTrip(id: string, updates: Partial<Trip>): Promise<Trip> {
    const { data, error } = await this.postgrest
      .from("trip")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update trip: ${error.message}`);
    }

    return data;
  }

  /**
   * Update trip with optimistic locking
   */
  async updateTripWithVersion(
    id: string,
    updates: Partial<Trip>,
    expectedVersion: number
  ): Promise<Trip> {
    const { data, error } = await this.postgrest
      .from("trip")
      .update({
        ...updates,
        version: expectedVersion + 1,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .eq("version", expectedVersion)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update trip: ${error.message}`);
    }

    if (!data) {
      throw new Error("VERSION_CONFLICT: Trip was modified by another user");
    }

    return data;
  }

  /**
   * Get a single trip by ID
   */
  async getTrip(id: string): Promise<Trip> {
    const { data, error } = await this.postgrest
      .from("trip")
      .select()
      .eq("id", id)
      .single();

    if (error) {
      throw new Error(`Failed to get trip: ${error.message}`);
    }

    return data;
  }

  /**
   * Get a trip with tenant details
   */
  async getTripWithTenant(id: string): Promise<TripWithTenant> {
    const { data, error } = await this.postgrest
      .from("trip_with_tenant")
      .select()
      .eq("trip_id", id)
      .single();

    if (error) {
      throw new Error(`Failed to get trip with tenant: ${error.message}`);
    }

    return data;
  }

  /**
   * List all trips for an FBO
   */
  async listTrips(fboId: string): Promise<Trip[]> {
    const { data, error } = await this.postgrest
      .from("trip")
      .select()
      .eq("fbo_id", fboId)
      .order("arrival_date", { ascending: true });

    if (error) {
      throw new Error(`Failed to list trips: ${error.message}`);
    }

    return data || [];
  }

  /**
   * List trips with tenant details for an FBO
   */
  async listTripsWithTenant(fboId: string): Promise<TripWithTenant[]> {
    const { data, error } = await this.postgrest
      .from("trip_with_tenant")
      .select()
      .eq("fbo_id", fboId)
      .order("arrival_date", { ascending: true });

    if (error) {
      throw new Error(`Failed to list trips with tenant: ${error.message}`);
    }

    return data || [];
  }

  /**
   * List all trips with tenant AND placement information for an FBO
   * This includes the current hangar/ramp assignment for each trip
   */
  async listTripsWithTenantAndPlacement(fboId: string): Promise<any[]> {
    // First get trips with tenant info
    const { data: trips, error: tripsError } = await this.postgrest
      .from("trip_with_tenant")
      .select()
      .eq("fbo_id", fboId)
      .order("arrival_date", { ascending: true });

    if (tripsError) {
      throw new Error(`Failed to list trips: ${tripsError.message}`);
    }

    if (!trips || trips.length === 0) {
      return [];
    }

    // Get all placement data for these trips in one query
    const tripIds = trips.map((t: any) => t.trip_id);
    const { data: placements, error: placementsError } = await this.postgrest
      .from("trip_placement_with_location")
      .select("trip_id, hangar_id, ramp_id, hangar_name, ramp_name, location_name, location_type")
      .in("trip_id", tripIds);

    if (placementsError) {
      console.error("Failed to fetch placements:", placementsError);
      // Return trips without placement data rather than failing
      return trips.map(trip => ({ ...trip, location_name: null, location_type: null }));
    }

    // Create a map of trip_id to placement for efficient lookup
    const placementMap = new Map();
    (placements || []).forEach(p => {
      // Use the most recent placement if there are multiple
      if (!placementMap.has(p.trip_id)) {
        placementMap.set(p.trip_id, p);
      }
    });

    // Merge trips with their placement data
    return trips.map(trip => {
      const placement = placementMap.get(trip.trip_id);
      return {
        ...trip,
        hangar_id: placement?.hangar_id || null,
        ramp_id: placement?.ramp_id || null,
        hangar_name: placement?.hangar_name || null,
        ramp_name: placement?.ramp_name || null,
        location_name: placement?.location_name || null,
        location_type: placement?.location_type || null,
      };
    });
  }

  /**
   * List trips for a specific tenant
   */
  async listTripsByTenant(tenantId: string): Promise<Trip[]> {
    const { data, error } = await this.postgrest
      .from("trip")
      .select()
      .eq("tenant_id", tenantId)
      .order("arrival_date", { ascending: true });

    if (error) {
      throw new Error(`Failed to list trips by tenant: ${error.message}`);
    }

    return data || [];
  }

  /**
   * List active trips (scheduled or active status) within a date range
   */
  async listActiveTrips(
    fboId: string,
    startDate: string,
    endDate: string
  ): Promise<Trip[]> {
    const { data, error } = await this.postgrest
      .from("trip")
      .select()
      .eq("fbo_id", fboId)
      .in("status", ["scheduled", "active"])
      .gte("departure_date", startDate)
      .lte("arrival_date", endDate)
      .order("arrival_date", { ascending: true });

    if (error) {
      throw new Error(`Failed to list active trips: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Delete a trip
   */
  async deleteTrip(id: string): Promise<void> {
    const { error } = await this.postgrest
      .from("trip")
      .delete()
      .eq("id", id);

    if (error) {
      throw new Error(`Failed to delete trip: ${error.message}`);
    }
  }

  /**
   * Update trip status
   *
   * NOTE: When a trip is cancelled (status = 'cancelled'), all hangar/ramp assignments
   * are automatically removed by the database trigger 'auto_unassign_cancelled_trips'.
   * See: src/web/schema/V0.45__auto_unassign_cancelled_trips.sql
   */
  async updateTripStatus(
    id: string,
    status: Trip["status"]
  ): Promise<Trip> {
    const { data, error } = await this.postgrest
      .from("trip")
      .update({
        status,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update trip status: ${error.message}`);
    }

    return data;
  }

  /**
   * Cancel a trip (soft delete)
   * Sets the trip status to 'cancelled', which automatically unassigns it from any location.
   */
  async cancelTrip(id: string): Promise<Trip> {
    return this.updateTripStatus(id, 'cancelled');
  }

  /**
   * Create a new trip with optional initial location assignment
   * 
   * This method atomically creates a trip and assigns it to a location in a single
   * database transaction. If no location is provided, an unassigned placement is
   * created covering the full trip duration.
   * 
   * @param trip - Trip data (must include tenant_id, fbo_id, arrival_date, departure_date)
   * @param locationId - Optional initial location ID (hangar or ramp)
   * @param locationType - Optional location type ('hangar' or 'ramp')
   * @returns Created trip with placement information
   * @throws Error if creation fails or location assignment fails
   */
  async createTripWithLocation(
    trip: Partial<Trip>,
    locationId?: string,
    locationType?: 'hangar' | 'ramp'
  ): Promise<Trip> {
    // Validate required fields
    if (!trip.tenant_id) {
      throw new Error("tenant_id is required");
    }
    if (!trip.fbo_id) {
      throw new Error("fbo_id is required");
    }
    if (!trip.arrival_date) {
      throw new Error("arrival_date is required");
    }
    if (!trip.departure_date) {
      throw new Error("departure_date is required");
    }

    // Validate date logic
    const arrival = new Date(trip.arrival_date);

    if (isNaN(arrival.getTime())) {
      throw new Error("Invalid arrival_date format");
    }

    // Skip departure validation if it's infinity (base tenant / indefinite stay)
    if (!isInfinityDate(trip.departure_date)) {
      const departure = new Date(trip.departure_date);

      if (isNaN(departure.getTime())) {
        throw new Error("Invalid departure_date format");
      }
      if (arrival >= departure) {
        throw new Error("arrival_date must be before departure_date");
      }
    }

    // Validate location parameters
    if (locationId && !locationType) {
      throw new Error("locationType is required when locationId is provided");
    }
    if (locationType && !['hangar', 'ramp'].includes(locationType)) {
      throw new Error("locationType must be 'hangar' or 'ramp'");
    }

    // Call the stored procedure
    const { data, error } = await this.postgrest.rpc('create_trip_with_location', {
      p_tenant_id: trip.tenant_id,
      p_fbo_id: trip.fbo_id,
      p_arrival_date: trip.arrival_date,
      p_departure_date: trip.departure_date,
      p_status: trip.status || 'scheduled',
      p_notes: trip.notes || null,
      p_confirmation_code: trip.confirmation_code || null,
      p_initial_location_id: locationId || null,
      p_initial_location_type: locationType || null
    });

    if (error) {
      throw new Error(`Failed to create trip with location: ${error.message}`);
    }

    if (!data || !(data as any).success) {
      throw new Error((data as any)?.error || 'Failed to create trip with location');
    }

    return (data as any).trip;
  }

  /**
   * Get all trips for a specific aircraft (by tail number)
   * 
   * @param fboId - FBO identifier
   * @param tailNumber - Aircraft tail number to search for
   * @returns Array of trips with tenant information for the aircraft
   */
  async getTripsByTailNumber(fboId: string, tailNumber: string): Promise<TripWithTenant[]> {
    const { data, error } = await this.postgrest
      .from("trip_with_tenant")
      .select()
      .eq("fbo_id", fboId)
      .eq("tail_number", tailNumber)
      .order("arrival_date", { ascending: true });

    if (error) {
      throw new Error(`Failed to get trips for aircraft ${tailNumber}: ${error.message}`);
    }

    return data || [];
  }
}